/* =========================================================
   DTF AI Modal — shell driver
   ---------------------------------------------------------
   On pages where the legacy pink "Create a design with Ninja AI"
   box is rendered (via the aiImageGenerator content-for), clicks
   on that box open this modal instead of the legacy
   <dialog id="generative-ai-dialog">.

   The chat UI inside the modal is the same .ai-chat panel used on
   /pages/ai-image-creator and the dtfut Tab 3 — it is driven by
   ai-chat-sandbox.js via instanceKey="dtfut", which routes
   "Use this design" clicks through the local cart/manage upload
   pipeline. This file owns ONLY the modal shell — open/close,
   focus restore, scroll-lock body class, and the post-select
   dismiss when a tile is used.
   ========================================================= */

(function () {
  'use strict';

  if (window.__dtfAiModalInit) return;
  window.__dtfAiModalInit = true;

  /* Relocate the modal element to <body> so it escapes any ancestor
     stacking context. The snippet is rendered deep inside bySizeNew.liquid
     (sections often have transforms / filters / will-change) which traps
     position:fixed children — visually that means the site header (which
     has its own z-index) can render *over* the modal. Moving #dtf-ai-modal
     to be a direct child of <body> gives our z-index: 100000 a clean
     stacking context. Done once, idempotent. */
  function relocateModalToBody() {
    var el = document.getElementById('dtf-ai-modal');
    if (!el) return;
    if (el.parentNode === document.body) return;
    document.body.appendChild(el);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relocateModalToBody);
  } else {
    relocateModalToBody();
  }

  /* Same fetch interception used in dtf-upload-tabs.js so the AI
     submit shows up to 3 cached canned results immediately and
     skips the slower postBatchGenerativeAiUpload when canned came
     back. Set once per page; safe to dedupe by checking the flag. */
  if (!window.__dtfutFetchPatched && typeof window.fetch === 'function') {
    window.__dtfutFetchPatched = true;
    var origFetch = window.fetch;
    window.fetch = function (url, options) {
      try {
        var urlStr = typeof url === 'string' ? url : (url && url.url) || '';

        if (urlStr.indexOf('getGenerativeAiUploads.php') !== -1) {
          return origFetch.apply(this, arguments).then(function (resp) {
            return resp.clone().json().then(function (data) {
              var capped = Array.isArray(data) ? data.slice(0, 3) : [];
              if (capped.length > 0) {
                window.__dtfutSkipNewAIGeneration = true;
              }
              return new Response(JSON.stringify(capped), {
                status: resp.status,
                headers: { 'Content-Type': 'application/json' }
              });
            }).catch(function () { return resp; });
          });
        }

        if (window.__dtfutSkipNewAIGeneration && urlStr.indexOf('postBatchGenerativeAiUpload.php') !== -1) {
          window.__dtfutSkipNewAIGeneration = false;
          return Promise.resolve(new Response('[]', {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          }));
        }
      } catch (e) { /* fall through */ }
      return origFetch.apply(this, arguments);
    };
  }

  /* ---------------- Modal open/close ---------------- */
  var modal = null;
  var lastFocused = null;

  function openModal() {
    if (!modal) modal = document.getElementById('dtf-ai-modal');
    if (!modal) return;
    lastFocused = document.activeElement;
    modal.hidden = false;
    /* Force a reflow so the transition runs from opacity:0. */
    void modal.offsetHeight;
    modal.classList.add('is-open');
    document.body.classList.add('dtf-ai-modal-open');
    var input = modal.querySelector('[data-ai-prompt]');
    if (input) {
      try { input.focus(); } catch (_) {}
    }
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('dtf-ai-modal-open');
    setTimeout(function () {
      if (!modal.classList.contains('is-open')) modal.hidden = true;
    }, 220);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus(); } catch (_) {}
    }
  }

  /* Capture-phase listener so it runs before generative-ai-image-modal.js's
     delegated jQuery handler on .generative-ai-area, which would otherwise
     call .showModal() on the legacy <dialog>. We stopImmediatePropagation()
     to short-circuit both other capture handlers and the bubble-phase
     jQuery delegation. */
  document.addEventListener('click', function (e) {
    var area = e.target && e.target.closest && e.target.closest('.generative-ai-area');
    if (!area) return;
    /* If the click is inside our modal, ignore — our modal contains the
       hidden .generative-ai-area from aiImageGenerator and we don't want
       a self-trigger. (CSS hides it; this is belt-and-braces.) */
    if (e.target.closest && e.target.closest('.dtf-ai-modal')) return;
    e.stopImmediatePropagation();
    e.preventDefault();
    openModal();
  }, true);

  document.addEventListener('click', function (e) {
    if (!modal || modal.hidden) return;
    if (e.target.closest('[data-dtf-ai-modal-close]')) {
      closeModal();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
  });

  /* When a tile is clicked inside the modal, ai-chat-sandbox.js handles the
     async upload via the cart pipeline. We just need to dismiss the modal
     so the user lands on the upload-state UI behind it. The upload runs in
     parallel — closing the modal does not cancel it. */
  document.addEventListener('click', function (e) {
    if (!modal || modal.hidden) return;
    var tile = e.target && e.target.closest && e.target.closest('.ai-pop-tile');
    if (!tile) return;
    if (!modal.contains(tile)) return;
    /* Skeleton/loading tiles aren't real picks. */
    if (tile.classList.contains('is-skeleton')) return;
    closeModal();
  });

  /* Same dismissal when the selection-bar "Use Design" / "Use Designs" CTA
     fires (multi/single-select via Recently Generated). The CTA's own
     handler in ai-chat-sandbox.js calls `stopPropagation()` to block the
     document-level outside-click closer for `#ai-panel` — that also
     prevents this listener from seeing the click in the bubble phase.
     Capture phase runs BEFORE the target's stopPropagation takes effect,
     so the modal closes synchronously and the CTA's own handler still
     does the upload. */
  document.addEventListener('click', function (e) {
    if (!modal || modal.hidden) return;
    var cta = e.target && e.target.closest && e.target.closest('.ai-chat-history-selection-bar__cta, [data-ai-selection-cta]');
    if (!cta) return;
    if (!modal.contains(cta)) return;
    closeModal();
  }, true);
})();
